import http from "../core/api/httpClient";
import {
  CaseCurrentStageDocumentsResponse,
  CaseCollatedDetailsResponse,
  LeadCurrentStageDocumentsResponse,
  LeadDocumentReviewPayload,
  LeadDocumentReviewResponse,
  CaseDocumentReviewResponse,
  CaseDocumentUploadResponse,
  LeadDocumentUploadResponse,
  DocumentDetailsRequestPayload,
  DocumentDetailsResponse,
  AdvisorAssignedTaskItem,
  AdvisorAssignedTaskListResponse,
  AdvisorActivityListResponse,
  AdvisorCaseTaskListResponse,
  AdvisorCaseEventListResponse,
  AdvisorCaseloadQuery,
  AdvisorCaseloadResponse,
  AdvisorDirectoryUser,
  AdvisorLeadEventListResponse,
  AdvisorLeadDetail,
  AdvisorLeadTaskListResponse,
  AdvisorUserProfile,
  AdvisorTaskStatus,
  ConsultationBookingPayload,
  ConsultationBookingResponse,
  ContactedLeadDetailsPayload,
  ContactedLeadDetailsResponse,
  ConsultationNoShowPayload,
  ConsultationNoShowResponse,
  EngagementLetterResponse,
  LeadCaptureDetailsPayload,
  LeadCaptureDetailsResponse,
  InitialLeadContactOutcomePayload,
  InitialLeadContactOutcomeResponse,
  LeadQualificationPayload,
  LeadQualificationResponse,
  QualifyingQuestionsEmailPayload,
  QualifyingQuestionsEmailResponse,
  AdvisorCaseStageTransitionPayload,
  AdvisorCaseStageTransitionResponse,
  AdvisorCaseClosePayload,
  AdvisorCaseCloseResponse,
} from "../types/advisor.types";
import { WorkflowTemplateResponse } from "../types/workflowTemplate.types";

const normalizeOptional = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export const advisorService = {
  async getAdvisor(advisorId: string): Promise<AdvisorUserProfile> {
    const res = await http.get<AdvisorUserProfile>(`/users/${advisorId}`);
    return res.data;
  },

  async listAdvisors(firmId: string): Promise<AdvisorDirectoryUser[]> {
    const res = await http.get<AdvisorUserProfile[]>("/users", {
      params: {
        firm_id: firmId,
        role: "ADVISOR",
        is_active: true,
        limit: 200,
        offset: 0,
      },
    });

    return res.data.map((item) => ({
      id: item.id,
      first_name: item.first_name,
      last_name: item.last_name,
    }));
  },

  async listAssistants(firmId: string): Promise<AdvisorDirectoryUser[]> {
    const res = await http.get<AdvisorUserProfile[]>("/users", {
      params: {
        firm_id: firmId,
        role: "ASSISTANT",
        is_active: true,
        limit: 200,
        offset: 0,
      },
    });

    return res.data.map((item) => ({
      id: item.id,
      first_name: item.first_name,
      last_name: item.last_name,
    }));
  },

  async getCaseload(
    query: AdvisorCaseloadQuery
  ): Promise<AdvisorCaseloadResponse> {
    const params: AdvisorCaseloadQuery = {
      lead_limit: query.lead_limit,
      lead_offset: query.lead_offset,
      case_limit: query.case_limit,
      case_offset: query.case_offset,
      lead_statuses: normalizeOptional(query.lead_statuses),
      case_stage_keys: normalizeOptional(query.case_stage_keys),
      case_stage_statuses: normalizeOptional(query.case_stage_statuses),
      include_closed_cases: query.include_closed_cases,
      q: normalizeOptional(query.q),
      visa_type: normalizeOptional(query.visa_type),
    };

    const res = await http.get<AdvisorCaseloadResponse>("/advisor/caseload", {
      params,
    });
    return res.data;
  },

  async getLeadDetail(leadId: string): Promise<AdvisorLeadDetail> {
    const res = await http.get<AdvisorLeadDetail>(`/leads/${leadId}`);
    return res.data;
  },

  async getWorkflowTemplate(templateId: string): Promise<WorkflowTemplateResponse> {
    const res = await http.get<WorkflowTemplateResponse>(`/workflow_templates/${templateId}`);
    return res.data;
  },

  async listWorkflowTemplates(params: {
    firm_id: string;
    visa_type?: string;
    country_code?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<WorkflowTemplateResponse[]> {
    const res = await http.get<WorkflowTemplateResponse[]>("/workflow_templates", {
      params: {
        firm_id: params.firm_id,
        visa_type: normalizeOptional(params.visa_type),
        country_code: normalizeOptional(params.country_code),
        is_active: params.is_active,
        limit: params.limit ?? 200,
        offset: params.offset ?? 0,
      },
    });
    return res.data;
  },

  async getLeadTasks(
    leadId: string,
    params?: { status?: AdvisorTaskStatus; limit?: number; offset?: number }
  ): Promise<AdvisorLeadTaskListResponse> {
    const res = await http.get<AdvisorLeadTaskListResponse>(`/leads/${leadId}/tasks`, {
      params: {
        status: params?.status,
        limit: params?.limit ?? 200,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async getLeadCurrentStageDocuments(leadId: string): Promise<LeadCurrentStageDocumentsResponse> {
    const res = await http.get<LeadCurrentStageDocumentsResponse>(
      `/leads/${leadId}/documents/current-stage`
    );
    return res.data;
  },

  async getCaseCurrentStageDocuments(caseId: string): Promise<CaseCurrentStageDocumentsResponse> {
    const res = await http.get<CaseCurrentStageDocumentsResponse>(
      `/cases/${caseId}/documents/current-stage`
    );
    return res.data;
  },

  async getCaseCollatedDetails(caseId: string): Promise<CaseCollatedDetailsResponse> {
    const res = await http.get<CaseCollatedDetailsResponse>(`/cases/${caseId}/details`);
    return res.data;
  },

  async uploadLeadDocument(
    leadId: string,
    documentItemId: string,
    file: File,
    uploadedBy?: string
  ): Promise<LeadDocumentUploadResponse> {
    const form = new FormData();
    form.append("file", file);
    if (uploadedBy) {
      form.append("uploaded_by", uploadedBy);
    }
    const res = await http.post<LeadDocumentUploadResponse>(
      `/leads/${leadId}/documents/${documentItemId}/upload`,
      form
    );
    return res.data;
  },

  async reviewLeadDocument(
    leadId: string,
    documentItemId: string,
    payload: LeadDocumentReviewPayload
  ): Promise<LeadDocumentReviewResponse> {
    const res = await http.post<LeadDocumentReviewResponse>(
      `/leads/${leadId}/documents/${documentItemId}/review`,
      payload
    );
    return res.data;
  },

  async reviewCaseDocument(
    caseId: string,
    documentItemId: string,
    payload: LeadDocumentReviewPayload
  ): Promise<CaseDocumentReviewResponse> {
    const res = await http.post<CaseDocumentReviewResponse>(
      `/cases/${caseId}/documents/${documentItemId}/review`,
      payload
    );
    return res.data;
  },

  async getDocumentDetails(
    payload: DocumentDetailsRequestPayload
  ): Promise<DocumentDetailsResponse> {
    const res = await http.post<DocumentDetailsResponse>("/document-details", payload);
    return res.data;
  },

  async uploadCaseDocument(
    caseId: string,
    documentItemId: string,
    file: File,
    uploadedBy?: string
  ): Promise<CaseDocumentUploadResponse> {
    const form = new FormData();
    form.append("file", file);
    if (uploadedBy) {
      form.append("uploaded_by", uploadedBy);
    }
    const res = await http.post<CaseDocumentUploadResponse>(
      `/cases/${caseId}/documents/${documentItemId}/upload`,
      form
    );
    return res.data;
  },

  async updateLeadTaskStatus(
    leadId: string,
    taskId: string,
    status: AdvisorTaskStatus,
    completedBy?: string
  ): Promise<void> {
    await http.patch(`/leads/${leadId}/tasks/${taskId}`, {
      status,
      completed_by: completedBy,
    });
  },

  async getCaseTasks(
    caseId: string,
    params?: { status?: AdvisorTaskStatus; limit?: number; offset?: number }
  ): Promise<AdvisorCaseTaskListResponse> {
    const res = await http.get<AdvisorCaseTaskListResponse>(`/cases/${caseId}/tasks`, {
      params: {
        status: params?.status,
        limit: params?.limit ?? 200,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async updateCaseTaskStatus(
    caseId: string,
    taskId: string,
    status: AdvisorTaskStatus,
    completedBy?: string
  ): Promise<void> {
    await http.patch(`/cases/${caseId}/tasks/${taskId}`, {
      status,
      completed_by: completedBy,
    });
  },

  async transitionCaseStage(
    caseId: string,
    payload: AdvisorCaseStageTransitionPayload
  ): Promise<AdvisorCaseStageTransitionResponse> {
    const res = await http.post<AdvisorCaseStageTransitionResponse>(
      `/cases/${caseId}/stage`,
      payload
    );
    return res.data;
  },

  async closeCase(
    caseId: string,
    payload?: AdvisorCaseClosePayload
  ): Promise<AdvisorCaseCloseResponse> {
    const res = await http.post<AdvisorCaseCloseResponse>(`/cases/${caseId}/close`, payload ?? {});
    return res.data;
  },

  async getCaseEvents(
    caseId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<AdvisorCaseEventListResponse> {
    const res = await http.get<AdvisorCaseEventListResponse>(`/cases/${caseId}/events`, {
      params: {
        limit: params?.limit ?? 20,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async getLeadEvents(
    leadId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<AdvisorLeadEventListResponse> {
    const res = await http.get<AdvisorLeadEventListResponse>(`/leads/${leadId}/events`, {
      params: {
        limit: params?.limit ?? 20,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async getAdvisorActivity(
    params?: { limit?: number; offset?: number }
  ): Promise<AdvisorActivityListResponse> {
    const res = await http.get<AdvisorActivityListResponse>("/advisor/activity", {
      params: {
        limit: params?.limit ?? 20,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async listAssignedTasks(
    params?: { status?: AdvisorTaskStatus; source?: "LEAD" | "CASE"; limit?: number; offset?: number }
  ): Promise<AdvisorAssignedTaskListResponse> {
    const res = await http.get<AdvisorAssignedTaskListResponse>("/users/me/tasks", {
      params: {
        status: params?.status,
        source: params?.source,
        limit: params?.limit ?? 200,
        offset: params?.offset ?? 0,
      },
    });
    return res.data;
  },

  async updateAssignedTaskStatus(
    task: AdvisorAssignedTaskItem,
    status: AdvisorTaskStatus,
    completedBy?: string
  ): Promise<void> {
    if (task.source === "CASE") {
      await this.updateCaseTaskStatus(task.source_id, task.task_id, status, completedBy);
      return;
    }
    await this.updateLeadTaskStatus(task.source_id, task.task_id, status, completedBy);
  },

  async updateLeadQualification(
    leadId: string,
    payload: LeadQualificationPayload
  ): Promise<LeadQualificationResponse> {
    const res = await http.post<LeadQualificationResponse>(
      `/leads/${leadId}/qualification`,
      {
        consultation_notes: normalizeOptional(payload.consultation_notes),
        qualification_outcome: payload.qualification_outcome,
        followup_reached_at: payload.followup_reached_at,
      }
    );
    return res.data;
  },

  async bookConsultation(
    leadId: string,
    payload: ConsultationBookingPayload
  ): Promise<ConsultationBookingResponse> {
    const res = await http.post<ConsultationBookingResponse>(
      `/leads/${leadId}/consultation/book`,
      {
        consultation_date: payload.consultation_date,
      }
    );
    return res.data;
  },

  async markConsultationNoShow(
    leadId: string,
    payload: ConsultationNoShowPayload
  ): Promise<ConsultationNoShowResponse> {
    const res = await http.post<ConsultationNoShowResponse>(
      `/leads/${leadId}/consultation/no-show`,
      {
        followup_outcome: payload.followup_outcome,
        consultation_notes: normalizeOptional(payload.consultation_notes),
      }
    );
    return res.data;
  },

  async sendEngagementLetter(leadId: string): Promise<EngagementLetterResponse> {
    const res = await http.post<EngagementLetterResponse>(
      `/leads/${leadId}/engagement-letter/send`
    );
    return res.data;
  },

  async updateContactedLeadDetails(
    leadId: string,
    payload: ContactedLeadDetailsPayload
  ): Promise<ContactedLeadDetailsResponse> {
    const normalizedDetails = payload.free_eligibility_consultation_details?.trim();
    const res = await http.post<ContactedLeadDetailsResponse>(
      `/leads/${leadId}/contacted/details`,
      {
        free_eligibility_consultation_offered:
          payload.free_eligibility_consultation_offered,
        free_eligibility_consultation_done_at:
          payload.free_eligibility_consultation_done_at,
        free_eligibility_consultation_details: normalizedDetails || undefined,
        updated_by: payload.updated_by,
      }
    );
    return res.data;
  },

  async sendQualifyingQuestionsEmail(
    leadId: string,
    payload?: QualifyingQuestionsEmailPayload
  ): Promise<QualifyingQuestionsEmailResponse> {
    const res = await http.post<QualifyingQuestionsEmailResponse>(
      `/leads/${leadId}/contacted/send-qualifying-questions-email`,
      {
        sent_by: payload?.sent_by,
      }
    );
    return res.data;
  },

  async updateLeadCaptureDetails(
    leadId: string,
    payload: LeadCaptureDetailsPayload
  ): Promise<LeadCaptureDetailsResponse> {
    const res = await http.patch<LeadCaptureDetailsResponse>(
      `/leads/${leadId}/capture-details`,
      {
        template_id: payload.template_id || undefined,
        source_channel: normalizeOptional(payload.source_channel),
        assigned_advisor_id: payload.assigned_advisor_id || undefined,
        assigned_assistant_id: payload.assigned_assistant_id || undefined,
        updated_by: payload.updated_by,
      }
    );
    return res.data;
  },

  async recordInitialLeadContactOutcome(
    leadId: string,
    payload: InitialLeadContactOutcomePayload
  ): Promise<InitialLeadContactOutcomeResponse> {
    const normalizedPhone = payload.phone?.trim();
    const normalizedNotes = payload.notes?.trim();
    const res = await http.post<InitialLeadContactOutcomeResponse>(
      `/leads/${leadId}/contact/outcome`,
      {
        outcome: payload.outcome,
        phone: normalizedPhone || undefined,
        notes: normalizedNotes || undefined,
        call_logged_at: payload.call_logged_at,
        contacted_by: payload.contacted_by,
      }
    );
    return res.data;
  },
};
